import { useAuth0 } from '@auth0/auth0-react';
import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Employee } from '../models/Employee';
import { useModal } from './ModalContext';
import { Organization } from '../models/Organization';
import { FortnoxLoader } from '../components/common/FortnoxLoader';
import { FortnoxIntegrationType } from '../types/backendTypes/FortnoxIntegrationType';
import { useNavigate } from 'react-router-dom';
import { AccessLevel } from '../types/AccessLevel';
import { useGetUser } from '../api/UserService/useGetUser';
import { useGetOrganization } from '../api/OrganizationService/useGetOrganization';
import { useSetupFortnoxTokens } from '../api/OrganizationService/useSetupFortnoxTokens';
import { useFortnoxImports } from '../hooks/useFortnoxImports';
import { LatestFortnoxImportRuns } from '../models/FortnoxImportRun';
import { isFortnoxImportActive } from '../utils/fortnoxImportRun';
import { buildFortnoxConnectErrorMessage } from '../utils/fortnoxImportMessage';
import { useAlert } from './AlertContext';
import { ErrorTypes } from '../types/backendResponses/ErrorTypes';
import { ApiError } from '../types/ApiError';

// Define the context value type
type UserContextType = {
  user: Employee | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  fortnoxBaseDataIsLoading: boolean;
  fortnoxSalaryDataIsLoading: boolean;
  isFortnoxBaseConnected: boolean;
  isFortnoxSalaryConnected: boolean;
  /** The latest Fortnox import per integration type, followed while it runs. */
  fortnoxImports: LatestFortnoxImportRuns;
  startFortnoxImport: (integrationType: FortnoxIntegrationType, full?: boolean) => Promise<void>;
  resumeFortnoxImport: (integrationType: FortnoxIntegrationType) => Promise<void>;
  retryFailedFortnoxImport: (integrationType: FortnoxIntegrationType) => Promise<void>;
  stopFortnoxImport: (integrationType: FortnoxIntegrationType) => Promise<void>;
};

// Create the context
export const UserContext = createContext<UserContextType | undefined>(undefined);

// Custom hook to use the UserContext
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Provider component
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: organisationData,
    action: checkOrgAPI,
    isLoading,
    error: organisationError,
  } = useGetOrganization([ErrorTypes.NotFound]);
  const { data: userData, action: getUser, isLoading: userLoading } = useGetUser();

  const [userDataHasBeenFetched, setUserDataHasBeenFetched] = useState<boolean>(false);

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [user, setUser] = useState<Employee | null>(null);
  const [isFortnoxBaseConnected, setIsFortnoxBaseConnected] = useState<boolean>(false);
  const [isFortnoxSalaryConnected, setIsFortnoxSalaryConnected] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const callbackUri = `${window.location.protocol}%2F%2F${window.location.host}`;
  const urlParams = new URLSearchParams(window.location.search);
  const authCode = urlParams.get('code');
  const { action: setupFortnoxBaseTokensInDb } = useSetupFortnoxTokens();
  const { action: setupFortnoxSalaryTokensInDb } = useSetupFortnoxTokens();
  // Only office staff follow and manage the imports; a cleaner neither polls them nor sees the
  // loader. The user is known after the organization, so office staff start following a moment later.
  const followsFortnoxImports =
    !!organisationData && !!user && user.accessLevel !== AccessLevel.Cleaner;
  const {
    runs: fortnoxImports,
    start: startFortnoxImport,
    resume: resumeFortnoxImport,
    retryFailed: retryFailedFortnoxImport,
    stop: stopFortnoxImport,
    lastChangeAt: fortnoxImportLastChangeAt,
  } = useFortnoxImports(followsFortnoxImports);
  const fortnoxBaseDataIsLoading = isFortnoxImportActive(fortnoxImports.base);
  const fortnoxSalaryDataIsLoading = isFortnoxImportActive(fortnoxImports.salary);

  const host = import.meta.env.VITE_REACT_APP_API_URL;

  const { open } = useModal();
  const { alert } = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if ((organisationError as ApiError | null)?.response?.data?.status === ErrorTypes.NotFound) {
      open('OrgSetup');
      return;
    }

    if (organisationData) {
      setIsFortnoxBaseConnected(organisationData.isFortnoxBaseConnected);
      setIsFortnoxSalaryConnected(organisationData.isFortnoxSalaryConnected);
      fetchUser();
    }
  }, [organisationData, isLoading, organisationError]);

  useEffect(() => {
    if (userLoading) {
      return;
    }

    if (!userData && userDataHasBeenFetched) {
      open('EmployeeSetup');
      return;
    }

    if (userData) {
      setUser(userData);
      if (userData.accessLevel === AccessLevel.Cleaner) {
        navigate('/employee/packing-list');
      }
    }
  }, [userData, userLoading, userDataHasBeenFetched]);

  useEffect(() => {
    const chosenFortnoxIntegration = sessionStorage.getItem('chosenFortnoxIntegration');

    const connecting =
      (chosenFortnoxIntegration === FortnoxIntegrationType.Base ||
        chosenFortnoxIntegration === FortnoxIntegrationType.Salary) &&
      user &&
      organisationData;

    // Fortnox sends the person back without a code when the login is cancelled or their
    // Fortnox user may not add integrations. Exchanging a missing code can only fail, so say
    // why instead.
    if (connecting && !authCode) {
      sessionStorage.setItem('chosenFortnoxIntegration', '');
      alert(
        'error',
        buildFortnoxConnectErrorMessage(urlParams.get('error'), urlParams.get('error_description')),
        undefined,
        20
      );
      return;
    }

    // get fortnox data for "base" aka articles, customers etc
    if (chosenFortnoxIntegration === FortnoxIntegrationType.Base && user && organisationData) {
      sessionStorage.setItem('chosenFortnoxIntegration', '');

      // get Fortnox tokens with auth code
      setupFortnoxBaseTokensInDb(authCode!, callbackUri, 'base').then((connected) => {
        if (!connected) return;
        // The organization was read before the tokens existed, so it still says not connected.
        setIsFortnoxBaseConnected(true);
        // start the import once the Fortnox tokens are fetched with the auth code; it runs as a
        // job in the backend and is followed by useFortnoxImports
        startFortnoxImport(FortnoxIntegrationType.Base);
      });
      // get fortnox data for "salary" aka employees etc
    } else if (
      chosenFortnoxIntegration === FortnoxIntegrationType.Salary &&
      user &&
      organisationData
    ) {
      sessionStorage.setItem('chosenFortnoxIntegration', '');

      // get Fortnox tokens with auth code
      setupFortnoxSalaryTokensInDb(authCode!, callbackUri, 'salary').then((connected) => {
        if (!connected) return;
        setIsFortnoxSalaryConnected(true);
        // start the import once the Fortnox tokens are fetched with the auth code
        startFortnoxImport(FortnoxIntegrationType.Salary);
      });
    }
  }, [organisationData, user, authCode]);

  // Function to fetch the current logged-in user
  const fetchUser = async () => {
    setLoading(true);
    setError(null);

    try {
      const success = await getUser();
      if (!success) {
        throw new Error('Failed to fetch user');
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message || 'Unknown error');
        setUser(null);
      }
    } finally {
      setLoading(false);
      setUserDataHasBeenFetched(true);
    }
  };

  // Refresh user's function
  const refreshUser = async () => {
    await fetchUser();
  };

  // Fetch user on initial load
  useEffect(() => {
    if (isAuthenticated) {
      checkOrgAPI();
    }
  }, [isAuthenticated]);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        error,
        refreshUser,
        fortnoxBaseDataIsLoading,
        fortnoxSalaryDataIsLoading,
        isFortnoxBaseConnected,
        isFortnoxSalaryConnected,
        fortnoxImports,
        startFortnoxImport,
        resumeFortnoxImport,
        retryFailedFortnoxImport,
        stopFortnoxImport,
      }}
    >
      <>
        {(fortnoxBaseDataIsLoading || fortnoxSalaryDataIsLoading) && (
          <FortnoxLoader
            runs={fortnoxImports}
            lastChangeAt={fortnoxImportLastChangeAt}
            onResume={resumeFortnoxImport}
            onStop={stopFortnoxImport}
          />
        )}
        {children}
      </>
    </UserContext.Provider>
  );
};
